import { Link } from "react-router-dom";
import PublicFooter from "@/components/PublicFooter";

const LOGO_URL = "https://customer-assets-gfyr7b9c.emergentagent.net/job_francophone-ops/artifacts/1lh4l6xi_3%20%281%29.PNG";
const EFFECTIVE = "February 27, 2026";
const CONTACT = "info@unitylanguagegroup.com";

export default function Privacy() {
  return (
    <div className="min-h-screen bg-slate-50" data-testid="privacy-page">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Unity Language Group" className="h-9 w-auto" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-red-700 font-bold">Unity Language Group</div>
              <div className="text-xs text-slate-500">Privacy Policy</div>
            </div>
          </Link>
          <Link to="/terms" className="text-xs text-slate-500 hover:text-slate-900 underline">Terms of Service →</Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10">
        <h1 className="font-display text-3xl font-bold text-slate-900 mb-1">Privacy Policy</h1>
        <div className="text-xs text-slate-500 mb-8">Effective {EFFECTIVE}</div>

        <Prose>
          <p>
            Unity Language Group, LLC ("<b>Unity</b>," "<b>we</b>," "<b>us</b>," or "<b>our</b>") is a
            Maryland-based language services provider that connects clients with francophone
            interpreters for USCIS, medical, legal, and other professional engagements. This
            Privacy Policy explains what personal information we collect, how we use and share it,
            and the rights you have. By using our services, our public application at
            <b> unitylanguagegroup.com </b>, or the Unity operations platform, you agree to this Policy.
          </p>

          <Section n="1" title="Information We Collect">
            <p><b>Information you provide directly.</b> When you apply to join our roster, engage us as a client, or communicate with us we collect:</p>
            <ul>
              <li>Contact details (name, email, phone, mailing address).</li>
              <li>Interpreter-specific information (language pairs, specialties, rates, availability, maximum travel distance, resume/credentials).</li>
              <li>Tax information required for 1099 contractor payments (SSN or EIN, W-9 form, tax address). This is stored encrypted at rest and is never displayed in emails or public pages.</li>
              <li>Client engagement information (organization, contact person, service type, USCIS receipt/A-number when provided by the client for case scheduling, appointment details).</li>
              <li>Signed contracts and electronic signatures (typed name, drawn signature image, IP address, browser user-agent, timestamp — retained as part of the signed record).</li>
            </ul>
            <p><b>Information we receive from third parties.</b> Payment status from Stripe; email delivery status from Resend; Gmail message metadata (sender, subject, body, received time) if you connect the Unity Communications Inbox to a Gmail account you control.</p>
            <p><b>Information collected automatically.</b> Basic access logs (IP, browser, referring URL, timestamps), a single session cookie required to keep you signed in, and email-open tracking pixels on emails we send through Resend.</p>
          </Section>

          <Section n="2" title="How We Use Information">
            <ul>
              <li>To provide interpretation services — scheduling assignments, matching interpreters, sending case briefs, and running our operations platform.</li>
              <li>To fulfill our tax and contractor obligations — issuing 1099-NEC forms when a contractor's cumulative earnings meet or exceed the federal threshold.</li>
              <li>To process payments through Stripe and to reconcile invoices.</li>
              <li>To send transactional and operational emails (contracts, invoices, calendar invitations, sign-in links, receipts). We do not send marketing email without your consent.</li>
              <li>To send SMS notifications where you have opted in — see the Terms of Service for details.</li>
              <li>To meet legal obligations, resolve disputes, and enforce our agreements.</li>
            </ul>
          </Section>

          <Section n="3" title="Medical (HIPAA-Adjacent) Interpretation">
            <p>
              For medical interpretation engagements, Unity may access Protected Health Information
              (PHI) incidental to interpreting between a healthcare provider and a limited-English-proficient
              patient. Unity is a downstream vendor and, where required, will enter into a Business
              Associate Agreement (BAA) with the covered entity or business associate that engages us.
              Interpreters are trained in HIPAA basics and are contractually required to hold PHI in
              strict confidence, use it solely for the interpretation session, and destroy any notes
              taken during the session.
            </p>
          </Section>

          <Section n="4" title="USCIS &amp; Legal Confidentiality">
            <p>
              Immigration case information — including receipt numbers, alien registration numbers,
              interview locations, and case narratives — is treated as strictly confidential. It is
              shared only with the assigned interpreter and their scheduling coordinator on a need-to-know
              basis, is never used for any purpose beyond the engagement, and is never sold or shared
              with data brokers. Interpreters are bound to attorney-client privilege standards where
              applicable.
            </p>
          </Section>

          <Section n="5" title="Interpreter 1099 Relationship">
            <p>
              Interpreters engaged through Unity are independent contractors, not employees. Unity
              retains W-9 forms and issues Form 1099-NEC when required by law. Interpreter tax
              identifiers are stored encrypted and are visible only to authorized Unity administrators.
              We do not sell or share interpreter contact information for marketing purposes.
            </p>
          </Section>

          <Section n="6" title="Payment Processing (Stripe)">
            <p>
              All card and bank payments are processed by Stripe, Inc. Unity never sees or stores
              full card numbers, CVCs, or bank account credentials — these are collected directly by
              Stripe on Stripe-hosted checkout pages and remain in Stripe's PCI-compliant systems.
              Stripe's privacy notice is at <a href="https://stripe.com/privacy" target="_blank" rel="noreferrer" className="text-blue-700 underline">stripe.com/privacy</a>.
            </p>
          </Section>

          <Section n="7" title="Cookies and Email Tracking Pixels">
            <p>We use a limited number of cookies and pixels:</p>
            <ul>
              <li><b>Session cookies</b> (<code>session_token</code>, <code>intp_session_token</code>) — required to keep you signed in. HttpOnly, Secure, SameSite=None or Lax. Without them, sign-in will not work.</li>
              <li><b>Email tracking pixels</b> — Resend (our email provider) inserts a 1x1 pixel to record whether an email we sent to you was opened. This helps us confirm operational emails such as contracts and payment reminders reached you.</li>
              <li>We do <b>not</b> use third-party advertising, retargeting, or cross-site tracking cookies.</li>
            </ul>
            <p>You can disable cookies in your browser but our sign-in and portal features will stop working.</p>
          </Section>

          <Section n="7A" title="SMS / Text Messages">
            <p>
              If you opt in to SMS messaging from Unity Language Group by providing your mobile
              number, we may send you text messages related to your assignments, appointments,
              invoices, and account status. Text messages are delivered through our provider,
              Twilio Inc.
            </p>
            <ul>
              <li><b>Message frequency.</b> Frequency varies with your activity — typically two to eight (2–8) messages per month.</li>
              <li><b>Message and data rates may apply.</b> Standard message and data rates from your mobile carrier may apply to each message you send or receive. Unity does not charge you for the SMS itself.</li>
              <li><b>Opting out.</b> Reply <b>STOP</b> to any message to opt out of further SMS. Reply <b>HELP</b> for help or contact us at <a href={`mailto:${CONTACT}`} className="text-blue-700 underline">{CONTACT}</a>.</li>
              <li><b>No sharing of mobile information.</b> Your mobile phone number and any information collected during the SMS opt-in process are used solely to deliver Unity's operational messages to you. We do <b>not</b> share, sell, rent, or disclose your mobile phone number or SMS opt-in data with any third parties or affiliates for marketing or promotional purposes. Sharing is limited to sub-processors (such as Twilio) strictly for message delivery.</li>
              <li><b>Consent is not a condition of purchase.</b> You are not required to consent to SMS to use Unity's other services.</li>
            </ul>
          </Section>

          <Section n="8" title="How We Share Information">
            <p>We do not sell personal information. We share limited information with:</p>
            <ul>
              <li>The specific interpreter assigned to your engagement (client information relevant to the assignment).</li>
              <li>Service providers acting on our behalf — Stripe (payments), Resend (email), Twilio (SMS, when applicable), Google (Gmail sync you initiate), and MongoDB Atlas / Emergent hosting infrastructure.</li>
              <li>Government tax authorities (IRS Form 1099-NEC reporting).</li>
              <li>Courts or law-enforcement when compelled by valid legal process.</li>
              <li>A successor in interest in the event of a merger, acquisition, or sale of assets.</li>
            </ul>
          </Section>

          <Section n="9" title="Data Retention">
            <p>
              We keep records for as long as they are necessary to provide services and to comply with
              tax, accounting, and legal obligations. Signed contracts and 1099 tax records are retained
              for at least seven (7) years. Communications and case notes are retained while your
              relationship with Unity is active and for a reasonable period after. You may request
              deletion of records not subject to a retention requirement (see Section 11).
            </p>
          </Section>

          <Section n="10" title="Security">
            <p>
              We use industry-standard controls: encrypted transport (TLS 1.2+), encryption at rest for
              tax identifiers and stored contracts, role-based access, cookie-based session tokens with
              short lifetimes, and HMAC-hashed one-time links for interpreter portal sign-in. No system
              is perfectly secure; you should report any suspected incident to us at the address below.
            </p>
          </Section>

          <Section n="11" title="Your Rights">
            <p>Depending on where you live, you may have the right to:</p>
            <ul>
              <li>Access the personal information we hold about you.</li>
              <li>Correct inaccurate information.</li>
              <li>Delete information we no longer need to retain.</li>
              <li>Object to certain uses or withdraw a previously granted consent.</li>
              <li>Receive a machine-readable copy of the information you provided to us.</li>
            </ul>
            <p>To exercise any right, email <a href={`mailto:${CONTACT}`} className="text-blue-700 underline">{CONTACT}</a>. We will respond within 30 days.</p>
          </Section>

          <Section n="12" title="Children">
            <p>Our services are not directed to children under 16 and we do not knowingly collect their information.</p>
          </Section>

          <Section n="13" title="International Users">
            <p>
              Unity operates from the United States and processes information there. If you access our
              services from outside the U.S., you consent to processing in the U.S. under U.S. law.
            </p>
          </Section>

          <Section n="14" title="Changes to This Policy">
            <p>We may update this Policy from time to time. Material changes will be posted here with a new "Effective" date, and we will notify active interpreters and clients by email where practical.</p>
          </Section>

          <Section n="15" title="Contact">
            <p>
              Unity Language Group, LLC<br />
              Maryland, USA<br />
              <a href={`mailto:${CONTACT}`} className="text-blue-700 underline">{CONTACT}</a>
            </p>
          </Section>
        </Prose>

        <PublicFooter />
      </main>
    </div>
  );
}

function Section({ n, title, children }) {
  return (
    <section className="mt-8" data-testid={`privacy-section-${n}`}>
      <h2 className="font-display text-lg font-bold text-slate-900 mb-2">{n}. {title}</h2>
      <div className="space-y-3 text-sm text-slate-700 leading-relaxed">{children}</div>
    </section>
  );
}

function Prose({ children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-8 text-sm text-slate-700 leading-relaxed [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-1 [&_code]:font-mono [&_code]:text-xs [&_code]:bg-slate-100 [&_code]:px-1 [&_code]:py-0.5 [&_code]:rounded">
      {children}
    </div>
  );
}
